import {HttpClient, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {catchError, filter, finalize, map, switchMap, take} from 'rxjs/operators';
import {SessionService} from '@core/services/session.service';
import {BehaviorSubject, throwError} from 'rxjs';
import {Constants} from '@app/app.constants';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    // Data.
    isRefreshing: boolean = false;
    token$ = new BehaviorSubject<string | null>(null);

    constructor(private _router: Router,
                private _http: HttpClient,
                private _session: SessionService) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler) {
        // Just return when the user is performing an refresh request.
        if (request.headers.has('X-No-Authorization')) {
            const newRequest = request.clone({headers: request.headers.delete('X-No-Authorization')});
            return next.handle(newRequest);
        }

        const newRequest = this.composeRequest(request, this.getToken(request));

        return next.handle(newRequest).pipe(
            catchError(error => {
                // Check if it is an error response.
                if (!(error instanceof HttpErrorResponse)) {
                    return throwError(error);
                }

                // Handle a 401 error.
                if (error.status === 401) {
                    return this.handle401Error(request, next);
                }

                // Just re-throw the error.
                return throwError(error);
            })
        );
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        // If already refreshing wait for the token to complete.
        if (this.isRefreshing) {
            return this.token$.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => next.handle(this.composeRequest(request, token)))
            );
        }

        // Set the refreshing to true.
        this.isRefreshing = true;
        this.token$.next(null);

        // Get the http observable.
        const refresh$ = this._http.get<Api.RefreshResponse>(Constants.endpoints.refresh, {
            headers: {
                'Authorization': `Bearer ${this._session.refreshToken}`,
                'X-No-Authorization': 'yes'
            }
        }).pipe(
            map(data => {
                // Get the token and refresh token from the response.
                const {token, refresh_token} = data;

                // Update the token in the local storage.
                this._session.setTokens(token, refresh_token);

                return token;
            })
        );

        return refresh$.pipe(
            switchMap(newToken => {
                if (!newToken) {
                    return this.logoutUser();
                }

                this.token$.next(newToken);
                return next.handle(this.composeRequest(request, newToken));
            }),
            catchError(error => this.logoutUser(error)),
            finalize(() => {
                this.isRefreshing = false;
            })
        );
    }

    private logoutUser(error?: HttpErrorResponse) {
        // Delete the tokens from the session.
        this._session.deleteTokensAndUser();

        // Route back.
        // Await the routing back to login.
        this._router.navigate(['/']).then(_ => _);

        // Throw the error.
        return throwError(error || 'Invalid');
    }

    private getToken = (request: HttpRequest<any>) => {
        // Get the token based on header.
        if (request.headers.get('Authorization') === 'refresh') {
            return this._session.refreshToken;
        }

        // Check if the user does not want authorization.
        if (request.headers.get('Authorization') === 'none') {
            return null;
        }

        // Return the default access token.
        return this._session.apiToken;
    };

    private composeRequest = (request: HttpRequest<any>, token: string | null = null) => {
        // If the token is not set, delete the authorization header.
        if (!token) {
            return request.clone({headers: request.headers.delete('Authorization')});
        }

        // Clone and add the authorization header.
        return request.clone({
            setHeaders: {Authorization: `Bearer ${token}`}
        });
    };

}

